import { useGetProgressData } from 'hooks/useGetProgressData';
import { useMemo } from 'react';
import { SpotData, AreaStatus } from 'types/spot';
import spot from 'data/spot.json';
import { CardItem, CardStatistics } from 'components/pages/Card/model/types';
import { MissionStatus, ProgressResponse } from 'store/progress';

function extractCardStatistics(
  spotList: SpotData[],
  progressData: ProgressResponse
): CardStatistics {
  const areasCompleted = progressData.completedAreaList.length;
  const areasAll = spotList.length;
  const missionsAll = spotList
    .flatMap(spot => spot.mission?.length ?? 0)
    .reduce((acc, curr) => acc + curr, 0);
  const missionsCompleted = progressData.missionStatusList.filter(
    mission => mission.completed
  ).length;
  return {
    areasCompleted,
    areasAll,
    missionsCompleted,
    missionsAll,
  };
}

function extractAreaCards(spotList: SpotData[], progressData: ProgressResponse): CardItem[] {
  return spotList.map(spot => {
    const areaStatus = progressData.areaStatusList.find(
      (item: AreaStatus) => String(item.areaNo) === spot.id
    );
    return {
      type: 'area',
      areaNo: Number(spot.id),
      id: `area-${spot.id}`,
      name: spot.name,
      scanCode: spot.scanCode,
      receivingPlace: spot.receivingPlace,
      status: {
        completed: areaStatus?.completed || false,
        receivedClearCard: areaStatus?.receivedClearCard || false,
      },
    };
  });
}

function extractMissionCards(spotList: SpotData[], progressData: ProgressResponse): CardItem[] {
  return spotList.flatMap<CardItem>(spot => {
    if (spot.mission) {
      return spot.mission?.map(mission => {
        const missionStatus = progressData.missionStatusList.find(
          (item: MissionStatus) => item.missionNo === mission.missionNo
        );
        return {
          type: 'mission',
          missionNo: Number(mission.missionNo),
          id: `mission-${mission.missionNo}`,
          name: mission.title,
          scanCode: mission.scanCode,
          receivingPlace: spot.receivingPlace,
          status: {
            completed: missionStatus?.completed || false,
            receivedClearCard: missionStatus?.receivedClearCard || false,
          },
        };
      });
    }
    return [];
  });
}

export function useCardListDataSource(): {
  cardStatistics: CardStatistics;
  areaCards: CardItem[];
  missionCards: CardItem[];
} {
  const { data: progressData } = useGetProgressData();
  const spotList: SpotData[] = spot as unknown as SpotData[];
  return useMemo(() => {
    if (!progressData)
      return {
        cardStatistics: {
          areasCompleted: 0,
          areasAll: 0,
          missionsCompleted: 0,
          missionsAll: 0,
        },
        areaCards: [],
        missionCards: [],
      };
    const cardStatistics = extractCardStatistics(spotList, progressData);
    const areaCards = extractAreaCards(spotList, progressData);
    const missionCards = extractMissionCards(spotList, progressData);
    return {
      cardStatistics,
      areaCards,
      missionCards,
    };
  }, [progressData, spotList]);
}
